import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import apiClient from '../api/client'

interface Topic {
  topic_id: number
  topic_name: string
  n_docs: number
  compactness: number | null
  redundancy: number | null
  effective_information_score: number | null
  keywords?: string[]
  documents?: { doc_id: number; x?: number; y?: number; cluster_id: number; preview: string }[]
  subclusters?: { subcluster_id: number; n_docs: number; keywords: string[]; doc_ids: number[] }[]
}

interface IterationAnalysis {
  iteration_index: number
  clustering_analysis: {
    global?: { n_documents: number; n_bertopic_topics: number; n_embedding_clusters: number; n_outliers: number }
    topics?: Topic[]
    embeddings_2d?: number[][]
  } | null
}

function AnalysisPage(): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const [iterations, setIterations] = useState<IterationAnalysis[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedIteration, setSelectedIteration] = useState<number>(0)
  const [selectedDoc, setSelectedDoc] = useState<{ doc_id: number; preview: string; page_content?: string; doi: string | null; openalex_id: string | null; topic_id: number; topic_name?: string; cluster_id: number } | null>(null)

  useEffect(() => {
    if (!id) return
    apiClient
      .get<IterationAnalysis[]>(`/research/runs/${id}/iterations`)
      .then((res) => {
        const data = res.data as unknown as IterationAnalysis[]
        setIterations(data)
        if (data.length > 0) setSelectedIteration(data[data.length - 1].iteration_index)
      })
      .catch(() => setError('Failed to load analysis'))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) return <div className="page-header"><h1 className="page-title">Loading analysis...</h1></div>
  if (error) return <div><p style={{ color: '#c00' }}>{error}</p><Link to={`/runs/${id}`}>Back to Run</Link></div>

  const currentIter = iterations.find((it) => it.iteration_index === selectedIteration) ?? iterations[iterations.length - 1]
  const topics = currentIter?.clustering_analysis?.topics ?? []
  const embeddings2d = currentIter?.clustering_analysis?.embeddings_2d ?? null

  const topicColors: Record<number, string> = {}
  const palette = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316']
  topics.forEach((t, i) => {
    topicColors[t.topic_id] = t.topic_id === -1 ? '#94a3b8' : palette[i % palette.length]
  })

  const getCoords = () => {
    if (!embeddings2d || embeddings2d.length === 0) return null
    const xs = embeddings2d.map((c) => c[0])
    const ys = embeddings2d.map((c) => c[1])
    const minX = Math.min(...xs), maxX = Math.max(...xs)
    const minY = Math.min(...ys), maxY = Math.max(...ys)
    const rangeX = maxX - minX || 1, rangeY = maxY - minY || 1
    return { minX, rangeX, minY, rangeY }
  }
  const coords = getCoords()

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Technical Analysis</h1>
          <p style={{ fontSize: 13, color: '#64748b' }}>Run {id?.slice(0, 8)} — wordcloud per topic and cluster scatter</p>
        </div>
        <Link to={`/runs/${id}`} className="page-action-secondary">Back to Run</Link>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {iterations.map((it) => (
          <button
            key={it.iteration_index}
            onClick={() => setSelectedIteration(it.iteration_index)}
            className={it.iteration_index === selectedIteration ? 'button-primary' : 'page-action-secondary'}
            style={{ padding: '6px 12px', fontSize: 13 }}
          >
            Iteration {it.iteration_index} {it.clustering_analysis?.global ? `(${it.clustering_analysis.global.n_documents} docs)` : ''}
          </button>
        ))}
      </div>

      <div className="detail-card detail-card-wide" style={{ marginBottom: 16 }}>
        <h2 className="detail-card-title">Hypothesis</h2>
        <div style={{ fontSize: 13, color: '#475569', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p><strong>Wordcloud:</strong> Top TF-IDF keywords per topic (from subclusters, 1–2 grams) sized by importance — larger = more discriminative for that topic. Validates the LLM-generated <em>topic_name</em>.</p>
          <p><strong>Scatter:</strong> UMAP (cosine) of <code>all-mpnet-base-v2</code> 768-d embeddings → 2D. Color = BERTopic, position = embedding cluster (Agglomerative, 0.25). Tight = high <em>compactness</em>, overlap = high <em>redundancy</em>; <em>effective = compactness·(1−redundancy)</em> should correlate with visual separation. Subcluster hulls are convex boundaries.</p>
        </div>
      </div>

      <div className="detail-card detail-card-wide">
        <h2 className="detail-card-title">Wordclouds per Topic — Iteration {selectedIteration}</h2>
        {topics.length === 0 ? (
          <p style={{ fontSize: 14, color: '#666' }}>No topics for this iteration.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {topics.map((t) => {
              const keywords = t.subclusters?.flatMap((sc) => sc.keywords) ?? []
              const uniqueKeywords = Array.from(new Set(keywords)).slice(0, 20)
              const displayKeywords = uniqueKeywords.length > 0 ? uniqueKeywords : t.topic_name.split(/\s+/).slice(0, 8)
              return (
                <div key={t.topic_id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                    <span>{t.topic_id === -1 ? 'Outliers' : t.topic_name}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>#{t.topic_id} · {t.n_docs} docs</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, display: 'flex', gap: 8 }}>
                    <span>compact {t.compactness?.toFixed(2) ?? '-'}</span>
                    <span>redund {t.redundancy?.toFixed(2) ?? '-'}</span>
                    <span style={{ fontWeight: 600 }}>eff {t.effective_information_score?.toFixed(2) ?? '-'}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minHeight: 60, border: '1px dashed #e2e8f0', borderRadius: 8, padding: 12, background: '#f8fafc' }}>
                    {displayKeywords.map((kw, i) => {
                      const size = 12 + (displayKeywords.length - i) * 0.6
                      const opacity = 0.6 + (displayKeywords.length - i) / displayKeywords.length * 0.4
                      return (
                        <span key={kw + i} style={{ fontSize: `${Math.max(10, Math.min(18, size))}px`, fontWeight: i < 3 ? 700 : 400, color: topicColors[t.topic_id], opacity }}>
                          {kw}
                        </span>
                      )
                    })}
                  </div>
                  {t.subclusters && t.subclusters.length > 0 && (
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
                      {t.subclusters.length} subclusters: {t.subclusters.map((sc) => `${sc.subcluster_id}(${sc.n_docs})`).join(', ')}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="detail-card detail-card-wide">
        <h2 className="detail-card-title">Cluster Scatter — UMAP 2D</h2>
        {!coords || !embeddings2d ? (
          <p style={{ fontSize: 14, color: '#666' }}>
            No 2D coordinates yet — run a new research after the backend upgrade to generate <code>embeddings_2d</code> (UMAP).
            {topics.flatMap((t) => t.documents ?? []).length > 0 && ` Showing ${topics.flatMap((t) => t.documents ?? []).length} docs as fallback grid.`}
          </p>
        ) : (
          <div style={{ overflow: 'auto' }}>
            <svg width={600} height={400} style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'white' }}>
              {(() => {
                const allDocs = topics.flatMap((t) => (t.documents ?? []).map((d) => ({ ...(d as unknown as Record<string, unknown>), topic_id: t.topic_id } as unknown as { doc_id: number; preview: string; page_content?: string; doi: string | null; openalex_id: string | null; cluster_id: number; topic_id: number; x?: number; y?: number })))
                return allDocs.map((d, idx) => {
                  const x = d.x, y = d.y
                  if (x == null || y == null) return null
                  const sx = ((x - coords!.minX) / coords!.rangeX) * 560 + 20
                  const sy = ((y - coords!.minY) / coords!.rangeY) * 360 + 20
                  const color = topicColors[d.topic_id] ?? '#64748b'
                  return (
                    <g key={d.doc_id + '-' + idx} onClick={() => setSelectedDoc({ doc_id: d.doc_id, preview: d.preview, page_content: d.page_content, doi: d.doi, openalex_id: d.openalex_id, topic_id: d.topic_id, topic_name: topics.find((t) => t.topic_id === d.topic_id)?.topic_name, cluster_id: d.cluster_id })} style={{ cursor: 'pointer' }}>
                      <circle cx={sx} cy={sy} r={5} fill={color} stroke="white" strokeWidth={1.5}>
                        <title>{`Doc ${d.doc_id}: ${d.preview?.slice(0, 60)} — topic ${d.topic_id}, cluster ${d.cluster_id} — click for details`}</title>
                      </circle>
                      <text x={sx + 7} y={sy - 7} fontSize={9} fill="#475569">{d.doc_id}</text>
                    </g>
                  )
                })
              })()}
            </svg>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
              {topics.map((t) => (
                <span key={t.topic_id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 12, height: 12, background: topicColors[t.topic_id], borderRadius: 2, display: 'inline-block' }} />
                  {t.topic_name} ({t.n_docs})
                </span>
              ))}
            </div>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>Hover a dot for doc preview. Axes are UMAP 1/2 (cosine, 768→2). Tight clusters = high compactness.</p>
          </div>
        )}
      </div>

      <div className="detail-card detail-card-wide">
        <h2 className="detail-card-title">Topic Metrics</h2>
        <table className="data-table" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Topic</th>
              <th style={{ textAlign: 'right' }}>Docs</th>
              <th style={{ textAlign: 'right' }}>Compact</th>
              <th style={{ textAlign: 'right' }}>Redund</th>
              <th style={{ textAlign: 'right' }}>Effective</th>
              <th>Clusters</th>
            </tr>
          </thead>
          <tbody>
            {topics.map((t) => (
              <tr key={t.topic_id}>
                <td>{t.topic_name} <span style={{ color: '#94a3b8' }}>#{t.topic_id}</span></td>
                <td style={{ textAlign: 'right' }}>{t.n_docs}</td>
                <td style={{ textAlign: 'right' }}>{t.compactness?.toFixed(3) ?? '-'}</td>
                <td style={{ textAlign: 'right' }}>{t.redundancy?.toFixed(3) ?? '-'}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{t.effective_information_score?.toFixed(3) ?? '-'}</td>
                <td style={{ fontSize: 11 }}>{(t as unknown as { embedding_clusters_present?: number[] }).embedding_clusters_present?.join(', ') ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedDoc && (
        <div onClick={() => setSelectedDoc(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, maxWidth: 720, width: '100%', maxHeight: '80vh', overflow: 'auto', padding: 24, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>Document {selectedDoc.doc_id} — {selectedDoc.topic_name} #{selectedDoc.topic_id}</h2>
              <button onClick={() => setSelectedDoc(null)} style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>Close</button>
            </div>
            <div style={{ fontSize: 13, color: '#475569', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {selectedDoc.doi && <div><strong>DOI:</strong> <a href={selectedDoc.doi.startsWith('http') ? selectedDoc.doi : `https://doi.org/${selectedDoc.doi}`} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>{selectedDoc.doi}</a></div>}
              {selectedDoc.openalex_id && <div><strong>OpenAlex:</strong> <a href={selectedDoc.openalex_id} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>{selectedDoc.openalex_id}</a></div>}
              <div><strong>Topic:</strong> {selectedDoc.topic_name} #{selectedDoc.topic_id} · Cluster {selectedDoc.cluster_id}</div>
              <div style={{ marginTop: 8 }}><strong>Document:</strong><p style={{ whiteSpace: 'pre-wrap', marginTop: 4, background: '#f8fafc', padding: 12, borderRadius: 8, maxHeight: 400, overflow: 'auto' }}>{(selectedDoc as unknown as { page_content?: string }).page_content ?? selectedDoc.preview}</p></div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AnalysisPage
